import { supabase } from "../supabase";

/**
 * 책에서 옮겨 적은 문장.
 *
 * memos 와 다르다. 기록은 내 감상이고 문장은 책의 말이다.
 * 남의 글을 공개해 두는 것이라 출처(책)를 반드시 함께 담는다.
 */

export const QUOTE_MAX = 300;

const SELECT = `
  id, user_id, book_id, text, page, is_public, created_at,
  books ( title, author ),
  quote_likes ( user_id )
`;

const fold = (r) => ({
  id: r.id,
  userId: r.user_id,
  bookId: r.book_id,
  text: r.text,
  page: r.page,
  isPublic: r.is_public,
  createdAt: r.created_at,
  bookTitle: r.books?.title ?? "",
  bookAuthor: r.books?.author ?? "",
  likes: (r.quote_likes ?? []).map((l) => l.user_id),
});

/** 한 사람이 모은 문장. RLS 가 남의 비공개 문장은 걸러준다. */
export async function listUserQuotes(userId) {
  const { data, error } = await supabase
    .from("quotes")
    .select(SELECT)
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(fold);
}

/** 한 책에 달린 문장 */
export async function listBookQuotes(bookId) {
  const { data, error } = await supabase
    .from("quotes")
    .select(SELECT)
    .eq("book_id", bookId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map(fold);
}

export async function addQuote({ userId, bookId, text, page, isPublic }) {
  const body = String(text ?? "").trim();
  if (!body) throw new Error("문장을 입력해주세요");
  const { data, error } = await supabase
    .from("quotes")
    .insert({
      user_id: userId,
      book_id: bookId,
      text: body.slice(0, QUOTE_MAX),
      page: page || null,
      is_public: isPublic !== false,
    })
    .select(SELECT)
    .single();
  if (error) throw error;
  return fold(data);
}

export async function updateQuote(id, patch) {
  const row = {};
  if (patch.text !== undefined) row.text = String(patch.text).trim().slice(0, QUOTE_MAX);
  if (patch.page !== undefined) row.page = patch.page || null;
  if (patch.isPublic !== undefined) row.is_public = patch.isPublic;
  const { error } = await supabase.from("quotes").update(row).eq("id", id);
  if (error) throw error;
}

export async function deleteQuote(id) {
  const { error } = await supabase.from("quotes").delete().eq("id", id);
  if (error) throw error;
}

/** 행성에 걸어둘 문장을 고른다. null 이면 걸어두지 않는다. */
export async function pinQuote(userId, quoteId) {
  const { error } = await supabase
    .from("profiles")
    .update({ pinned_quote_id: quoteId })
    .eq("id", userId);
  if (error) throw error;
}

/**
 * '오늘의 문장' 이 고를 후보.
 *
 * 예전에는 프로필마다 하나뿐이라 users 목록에 실려 왔다. 이제는 사람마다
 * 여러 개라 따로 가져온다. 무작위 정렬을 서버에 맡길 수 없으므로
 * 최근 것부터 넉넉히 받아 화면에서 고른다 — 지금 규모에서는 충분하다.
 */
export async function listPublicQuotes(limit = 200) {
  const { data, error } = await supabase
    .from("quotes")
    // quotes 와 profiles 사이에 관계가 둘이다(작성자, 걸어둔 문장).
    // FK 를 짚지 않으면 어느 쪽인지 모호해 300 이 난다.
    .select(`${SELECT}, profiles!quotes_user_id_fkey ( name, avatar )`)
    .eq("is_public", true)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []).map((r) => ({
    ...fold(r),
    name: r.profiles?.name ?? "",
    avatar: r.profiles?.avatar ?? "🌿",
  }));
}

/** 좋아요 토글. memos 의 toggleLike 와 같은 방식이다. */
export async function toggleQuoteLike(quoteId, userId, currentlyLiked) {
  if (currentlyLiked) {
    const { error } = await supabase
      .from("quote_likes")
      .delete()
      .eq("quote_id", quoteId)
      .eq("user_id", userId);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from("quote_likes")
      .upsert({ quote_id: quoteId, user_id: userId }, { onConflict: "quote_id,user_id" });
    if (error) throw error;
  }
}
